import { HOLD_CLEANING_INFORMATION_OPTIONS } from "../config/information-filter.constants";
import type { HoldCleaningInformationFilterProps } from "../models/hold-cleaning.models";
import { useEffect, useRef } from "react";

export const HoldCleaningInformationFilter = ({
  selected,
  counts,
  loading,
  onChange,
}: HoldCleaningInformationFilterProps) => {
  const detailsRef = useRef<HTMLDetailsElement>(null);

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      if (!detailsRef.current?.contains(event.target as Node)) {
        detailsRef.current?.removeAttribute("open");
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && detailsRef.current?.open) {
        detailsRef.current.removeAttribute("open");
        detailsRef.current.querySelector("summary")?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  return (
    <details ref={detailsRef} className="relative">
      <summary className="cursor-pointer rounded-lg border border-[#C7D0CE] bg-white px-3 py-2.5 text-sm text-[#292F2D] shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#1F8475] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
        Has information: {selected.length ? `${selected.length} selected` : "All"}
      </summary>
      <div className="absolute right-0 z-20 mt-2 w-80 max-w-[90vw] rounded-lg border border-[#C7D0CE] bg-white p-4 text-[#292F2D] shadow-lg dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
        <fieldset>
          <legend className="text-sm font-semibold">Vessel information</legend>
          <p id="hold-cleaning-information-help" className="my-2 text-xs text-[#4A5655] dark:text-gray-300">
            Requires all selected options. Counts show vessels in this search/tab before these selections. Only the
            previous-voyages photo option looks in history.
          </p>
          {HOLD_CLEANING_INFORMATION_OPTIONS.map((option) => (
            <label
              key={option.value}
              title={option.description}
              className="flex cursor-pointer items-center gap-2 rounded py-2 text-sm text-[#292F2D] dark:text-gray-100"
            >
              <input
                type="checkbox"
                disabled={option.value === "PHOTOS_HISTORY" && counts?.PHOTOS_HISTORY === undefined}
                checked={selected.includes(option.value)}
                aria-describedby="hold-cleaning-information-help"
                onChange={(event) =>
                  onChange(
                    event.target.checked
                      ? [...selected, option.value]
                      : selected.filter((value) => value !== option.value),
                  )
                }
                className="accent-[#1F8475]"
              />
              <span>{option.label}</span>
              <span
                aria-label={`${option.label} vessel count`}
                className="ml-auto rounded-full bg-[#DFF2F0] px-2 py-0.5 text-xs text-[#176B62] dark:bg-teal-950 dark:text-teal-200"
              >
                {loading ? "—" : counts?.[option.value]?.toLocaleString() ?? "—"}
              </span>
            </label>
          ))}
        </fieldset>
        <button
          type="button"
          disabled={!selected.length}
          onClick={() => onChange([])}
          className="mt-2 text-sm font-semibold text-[#176B62] disabled:opacity-50 dark:text-teal-200"
        >
          Clear filters
        </button>
      </div>
    </details>
  );
};
